import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { useFetchProductHook } from 'hooks/products/useFetchProductHook';
import { client } from 'utilities/db';
import { ModalComponent } from 'components/modal';
import { TextField } from 'components/input';
import { Button } from 'components/button';
import { Toasts } from 'components/toasts';
import { getBranchId, getLocationId } from 'utilities/helpers/auth';
import {
  useFetchRecipeProductCategories,
  useFetchRecipeProductGroups
} from 'hooks/recipes/useRecipesHook';
import { CustomSelectDropdown } from 'components/select/multi';
import { useFetchProductsHook } from 'hooks/products/useFetchProductsHook';
import { useStore } from 'store';
import CreatableSelect from 'react-select/creatable';

export const ProductItselfPage = ({
  action,
  setAction,
  setAdditionType,
  selectedProduct,
  setSelectedProduct
}) => {
  const { productData, productLoading, productRefetch } = useFetchProductHook(
    `product/${selectedProduct}`,
    selectedProduct
  );

  const { brand } = useStore((state) => state);

  const branchId = getBranchId();

  const { data: groupsData } = useFetchRecipeProductGroups(branchId);
  const { data: categoriesData } = useFetchRecipeProductCategories(branchId);

  const { productsData, productsRefetch } = useFetchProductsHook(
    `products/${brand?.id}/${branchId}`,
    brand?.id,
    branchId
  );

  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
    control,
    watch
  } = useForm({
    defaultValues: {
      product_name: '',
      unit: '',
      current_unit_price: '',
      upc: '',
      product_category: '',
      product_group: ''
    }
  });

  useMemo(() => {
    if (action === 'update' && productData && categoriesData && groupsData) {
      // Find the category and group names based on IDs
      const categoryName =
        categoriesData?.find(
          (item) => item?.id === productData?.product_category_id
        )?.category_name || '';

      const groupName =
        groupsData?.find((item) => item?.id === productData?.product_group_id)
          ?.group_name || '';

      reset({
        product_name: productData?.product_name || '',
        unit: productData?.unit || '',
        current_unit_price: productData?.current_unit_price || '',
        upc: productData?.upc || '',
        product_category: categoryName,
        product_group: groupName
      });
    } else if (action === 'create' || !action) {
      // Reset form for create mode
      reset({
        product_name: '',
        unit: '',
        current_unit_price: '',
        upc: '',
        product_category: '',
        product_group: ''
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, productData, categoriesData, groupsData]);

  const { mutate, isLoading, isError, error, isSuccess } = useMutation(
    (groupData) => {
      if (action === 'update') {
        return client.post(`/product/update/${selectedProduct}`, groupData);
      } else {
        return client.post('/product/add', groupData);
      }
    }
  );

  const handleSubmitData = (data) => {
    const {
      product_name,
      unit,
      current_unit_price,
      quantity,
      product_group,
      product_category,
      current_quantity,
      upc
    } = data;

    // Handle both string values and object values for category and group
    const categoryValue =
      typeof product_category === 'string'
        ? product_category
        : product_category?.label || product_category?.value;

    const groupValue =
      typeof product_group === 'string'
        ? product_group
        : product_group?.label || product_group?.value;

    // For update, we need to find the IDs based on names
    let categoryId = null;
    let groupId = null;

    if (action === 'update') {
      // Find category ID by name
      const foundCategory = categoriesData?.find(
        (item) => item?.category_name === categoryValue
      );
      categoryId = foundCategory?.id;

      // Find group ID by name
      const foundGroup = groupsData?.find(
        (item) => item?.group_name === groupValue
      );
      groupId = foundGroup?.id;
    }

    const submitData = {
      product_name: product_name,
      // unit: unit?.value,
      current_unit_price: 0,
      product_group: groupValue,
      product_category: categoryValue,
      brand_id: brand?.id,
      branch_id: branchId,
      quantity: 0,
      upc: upc,
      raw_information: {}
    };

    // Add IDs for update case
    if (action === 'update' && selectedProduct) {
      submitData.id = selectedProduct;
      if (categoryId) submitData.product_category_id = categoryId;
      if (groupId) submitData.product_group_id = groupId;
    }

    mutate(submitData);
  };

  const closeModal = () => {
    setAction('');
    setAdditionType('');
    setSelectedProduct(null);
  };

  useMemo(() => {
    if (isSuccess) {
      productsRefetch();
      productRefetch();
      Toasts.success(
        action === 'update'
          ? 'Product updated successfully'
          : 'Product added successfully'
      );
      closeModal();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  useEffect(() => {
    if (!isError) return;
    Toasts.error(
      error?.response?.data?.message?.[0]?.msg || error?.response?.data?.message
    );
  }, [isError]);

  return (
    <>
      <ModalComponent
        modal={closeModal}
        handleModal={closeModal}
        modalMiddle={false}
        rightModal={true}
      >
        <h4>
          <span className="ff_semi">
            {action === 'update' ? 'Update' : 'Add New'} Product
          </span>
        </h4>
        <div className="mt_40"></div>
        <div className="pt_10">
          <form onSubmit={handleSubmit(handleSubmitData)}>
            <Controller
              name="product_category"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <div
                  style={{
                    width: '100%',
                    background: 'white',
                    padding: '10px',
                    borderRadius: '5px'
                  }}
                >
                  <label>Product Category</label>
                  <div className="mt_10"></div>
                  <CreatableSelect
                    {...field}
                    isClearable
                    isSearchable
                    placeholder="Type New or Choose Product Category"
                    options={categoriesData?.map((item) => ({
                      value: item?.id,
                      label: item?.category_name
                    }))}
                    value={
                      field.value
                        ? {
                            value: field.value,
                            label: field.value
                          }
                        : null
                    }
                    onChange={(option) => {
                      field.onChange(option ? option.label : '');
                    }}
                    onCreateOption={(inputValue) => {
                      field.onChange(inputValue);
                    }}
                  />
                  {errors?.product_category?.type === 'required' && (
                    <div
                      style={{
                        color: 'red',
                        fontSize: '12px',
                        marginTop: '5px'
                      }}
                    >
                      Product Category is required
                    </div>
                  )}
                </div>
              )}
            />
            <div className="mt_16"></div>
            <Controller
              name="product_group"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <div
                  style={{
                    width: '100%',
                    background: 'white',
                    padding: '10px',
                    borderRadius: '5px'
                  }}
                >
                  <label>Product Group</label>
                  <div className="mt_10"></div>
                  <CreatableSelect
                    {...field}
                    isClearable
                    isSearchable
                    placeholder="Type New or Choose Product Group"
                    options={groupsData?.map((item) => ({
                      value: item?.id,
                      label: item?.group_name
                    }))}
                    value={
                      field.value
                        ? {
                            value: field.value,
                            label: field.value
                          }
                        : null
                    }
                    onChange={(option) => {
                      field.onChange(option ? option.label : '');
                    }}
                    onCreateOption={(inputValue) => {
                      field.onChange(inputValue);
                    }}
                  />
                  {errors?.product_group?.type === 'required' && (
                    <div
                      style={{
                        color: 'red',
                        fontSize: '12px',
                        marginTop: '5px'
                      }}
                    >
                      Product Group is required
                    </div>
                  )}
                </div>
              )}
            />
            {/* The `TextField` component in the code snippet is rendering an input field for entering
            the product category information. Here is a breakdown of what each attribute is doing: */
            /* <TextField
              label="Product Category"
              placeholder="Product Category"
              name="product_category"
              error={
                errors?.product_category?.type === 'required'
                  ? 'Product Category is required'
                  : ''
              }
              register={register}
              validation={{
                required: true
              }}
              required={true}
            /> */}

            {/* <TextField
              label="Product Group"
              placeholder="Product Group"
              name="product_group"
              error={
                errors?.product_group?.type === 'required'
                  ? 'Product Group is required'
                  : ''
              }
              register={register}
              validation={{
                required: true
              }}
              required={true}
            /> */}
            <div className="mt_16"></div>
            <TextField
              label="Product Name"
              placeholder="Product Name"
              name="product_name"
              error={
                errors?.product_name?.type === 'required'
                  ? 'Product Name is required'
                  : ''
              }
              register={register}
              validation={{
                required: true
              }}
              required={true}
            />
            {/* <div className="mt_16"></div>
            <Controller
              name="unit"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <CustomSelectDropdown
                  hookForm={true}
                  field={field}
                  label="Unit"
                  placeholder="Choose options"
                  options={[
                    'kg',
                    'pounds',
                    'grams',
                    'ounces',
                    'liters',
                    'teaspoon',
                    'number/pieces',
                    'tablespoon',
                    'dozen'
                  ]?.map((unit) => ({
                    value: unit,
                    label: unit?.toUpperCase() || '-'
                  }))}
                  error={
                    errors?.unit?.type === 'required' ? 'Unit is required' : ''
                  }
                  required={true}
                />
              )}
            /> */}
            <div className="mt_16"></div>
            <TextField
              label="Product UPC"
              placeholder="Product upc"
              name="upc"
              error={
                errors?.upc?.type === 'required'
                  ? 'Product UPC is required'
                  : errors?.upc?.type === 'pattern'
                  ? 'Product UPC can only contain numbers and capital letters'
                  : ''
              }
              register={register}
              validation={{
                required: true,
                pattern: {
                  value: /^[0-9A-Z]+$/,
                  message:
                    'Product UPC can only contain numbers and capital letters'
                }
              }}
              required={true}
            />
            {/* <TextField
              label="Unit"
              placeholder="Unit"
              name="unit"
              error={
                errors?.unit?.type === 'required' ? 'Unit is required' : ''
              }
              register={register}
              validation={{
                required: true
              }}
            /> */}
            {/* <TextField
              label="Current Unit Price"
              placeholder="Current Unit Price"
              name="current_unit_price"
              error={
                errors?.current_unit_price?.type === 'required'
                  ? 'Current Unit Price is required'
                  : errors?.current_unit_price?.type === 'pattern'
                  ? 'Current Unit Price is invalid, should be like 10 or 10.00'
                  : ''
              }
              register={register}
              validation={{
                required: true
              }}
              type="number"
              step="0.01"
              required={true}
            />
            <TextField
              label="Current Quantity"
              placeholder="Current Quantity"
              name="current_quantity"
              error={
                errors?.current_quantity?.type === 'required'
                  ? 'Current Quantity is required'
                  : ''
              }
              register={register}
              validation={{
                required: true
              }}
              type="number"
              required={true}
            /> */}
            <div className="mt_16"></div>

            {/* <Controller
              name="product_category"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <CustomSelectDropdown
                  hookForm={true}
                  isMulti={false}
                  field={field}
                  label="Product Categories"
                  placeholder="Choose Product Category"
                  options={categoriesData?.map((item) => ({
                    value: item?.id,
                    label: item?.category_name
                  }))}
                  error={
                    errors?.product_category?.type === 'required'
                      ? 'product Category is required'
                      : ''
                  }
                  required={true}
                />
              )}
            /> */}
            <div className="mt_30"></div>
            <hr className="hr_modal_right" />
            <div className="flex item_hend gap_16 mt_30">
              <Button
                onClick={closeModal}
                color="outline-lite"
                label={'Cancel'}
              />
              <Button
                label={action === 'update' ? 'Update' : 'Submit'}
                color="bg-primary"
                type="submit"
                disabled={
                  isLoading || (action === 'update' ? productLoading : false)
                }
              />
            </div>
          </form>
        </div>
      </ModalComponent>
    </>
  );
};
